import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';

interface User {
  userId: string;
  email: string;
  username: string;
  role: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  idToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { accessToken: string; refreshToken: string; idToken: string } }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'LOGOUT' }
  | { type: 'TOKEN_REFRESH_SUCCESS'; payload: { accessToken: string; idToken: string } };

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  idToken: null,
  isAuthenticated: false,
  isLoading: true,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };

    case 'LOGIN_SUCCESS': {
      const user = parseJWT(action.payload.idToken);
      return {
        ...state,
        user,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        idToken: action.payload.idToken,
        isAuthenticated: true,
        isLoading: false,
      };
    }

    case 'LOGIN_FAILURE':
      return { ...initialState, isLoading: false };

    case 'LOGOUT':
      return { ...initialState, isLoading: false };

    case 'TOKEN_REFRESH_SUCCESS': {
      const user = parseJWT(action.payload.idToken);
      return {
        ...state,
        user,
        accessToken: action.payload.accessToken,
        idToken: action.payload.idToken,
        isAuthenticated: true,
      };
    }

    default:
      return state;
  }
}

function parseJWT(token: string): User | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(jsonPayload);

    return {
      userId: payload.sub,
      email: payload.email || '',
      username: payload['cognito:username'] || payload.username || '',
      role: payload['custom:role'] || 'user',
    };
  } catch (error) {
    console.error('Error parsing JWT:', error);
    return null;
  }
}

interface AuthContextValue extends AuthState {
  login: (accessToken: string, refreshToken: string, idToken: string) => void;
  logout: () => void;
  refreshTokens: (accessToken: string, idToken: string) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Cargar tokens desde localStorage al iniciar
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    const idToken = localStorage.getItem('idToken');

    if (accessToken && refreshToken && idToken) {
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: { accessToken, refreshToken, idToken },
      });
    } else {
      dispatch({ type: 'LOGIN_FAILURE' });
    }
  }, []);

  const login = (accessToken: string, refreshToken: string, idToken: string) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('idToken', idToken);

    dispatch({
      type: 'LOGIN_SUCCESS',
      payload: { accessToken, refreshToken, idToken },
    });
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');

    dispatch({ type: 'LOGOUT' });
  };

  const refreshTokens = (accessToken: string, idToken: string) => {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('idToken', idToken);

    dispatch({
      type: 'TOKEN_REFRESH_SUCCESS',
      payload: { accessToken, idToken },
    });
  };

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refreshTokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
